/**
 * API 请求统一封装
 * ------------------------------------------------------------------
 * 所有请求都走 dev server 代理（见 vue.config.js），避免浏览器跨域：
 *   /api     -> 测试服 http://aitextapi.yidaoo.cn / 正式服 https://ai-prod-api.yidaoo.cn（业务接口）
 *   /upload  -> https://mh-up-prod.yidaoo.cn （文件上传）
 * 代理会把前缀去掉再转发，所以这里的 URL 与后端真实路径一致。
 * 之后若要换域名/环境，只改本文件的 USE_PROD_API 开关（或 vue.config.js）即可。
 */

// 开发环境走 vue.config.js 代理（/api、/upload 前缀会被去掉再转发）；
// 生产环境没有代理，需直接请求后端真实域名，否则会被前端服务器当作页面路由返回 index.html。
var IS_DEV = process.env.NODE_ENV === 'development'

// ── 业务接口环境切换开关 ──
// false -> 测试服 http://aitextapi.yidaoo.cn
// true  -> 正式服 https://ai-prod-api.yidaoo.cn
// 注意：开发环境默认走 /api 本地代理，代理目标在 vue.config.js 中配置，
//       若开发时也要切环境，请同步修改 vue.config.js 里的 USE_PROD_API。
// var USE_PROD_API = false
var USE_PROD_API = true

var API_HOST = USE_PROD_API
  ? 'https://ai-prod-api.yidaoo.cn'
  : 'http://aitextapi.yidaoo.cn'

var API_BASE = IS_DEV ? '/api' : API_HOST // 业务接口前缀
var UPLOAD_BASE = IS_DEV ? '/upload' : 'https://mh-up-prod.yidaoo.cn' // 上传接口前缀

function authHeaders() {
  var h = {}
  var t = localStorage.getItem('sb-access-token')
  if (t) h['token'] = t
  return h
}

// 通用 GET（支持查询参数）
async function get(url, params) {
  if (params && typeof params === 'object') {
    var qs = Object.keys(params)
      .filter(function(k) { return params[k] !== undefined && params[k] !== null })
      .map(function(k) { return encodeURIComponent(k) + '=' + encodeURIComponent(params[k]) })
      .join('&')
    if (qs) url += (url.indexOf('?') === -1 ? '?' : '&') + qs
  }
  var res = await fetch(url, { method: 'GET', headers: authHeaders() })
  return res.json()
}

// 通用 POST
async function post(url, data) {
  var res = await fetch(url, {
    method: 'POST',
    headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
    body: JSON.stringify(data)
  })
  return res.json()
}

// ── 认证（走 /api 代理） ──
export function sendVerifyCode(emailAddress) {
  return post(API_BASE + '/verifyCode/send', { emailAddress })
}
export function emailLogin(emailAddress, verifyCode) {
  return post(API_BASE + '/user/emailLogin', { emailAddress, verifyCode })
}
export function pwdLogin(userName, userPwd) {
  return post(API_BASE + '/user/pwdLogin', { userName, userPwd })
}
export function checkEmail(emailAddress, verifyCode) {
  return post(API_BASE + '/user/checkEmail', { emailAddress, verifyCode })
}
export function setPassword(userPwd, checkPwd) {
  return post(API_BASE + '/user/userPwdRegister', { userPwd, checkPwd })
}

// ── 灵感广场 / 模型 / 生成（走 /api 代理） ──
export function getPlate() {
  return get(API_BASE + '/ai/getPlate')
}
export function getPlateDetail(page, size, tag) {
  return get(API_BASE + '/ai/getPlateDetail', { page: page, size: size, tag: tag })
}
// 我的收藏：GET /user/getUserCollectList，传参 page、size，返回结构与灵感广场一致
export function getUserCollectList(page, size) {
  return get(API_BASE + '/user/getUserCollectList', { page: page, size: size })
}
export function getDetail(id) {
  return get(API_BASE + '/ai/getDetail', { id })
}
// 收藏灵感：POST /user/collect，传参 sourceId（灵感 id）
export function collectInspiration(sourceId) {
  return post(API_BASE + '/user/collect', { sourceId: sourceId })
}
// 取消收藏：POST /user/cancelCollect，传参 sourceId（灵感 id）
export function cancelCollectInspiration(sourceId) {
  return post(API_BASE + '/user/cancelCollect', { sourceId: sourceId })
}

// 充值套餐列表：GET /api/pay/coinList
export function getCoinList() {
  return get(API_BASE + '/api/pay/coinList')
}
// 下单：POST /api/pay/payBox，payType: ali_h5/weixin_h5，eventId: 套餐ID
export function payBox(payType, eventId) {
  return post(API_BASE + '/api/pay/payBox', { payType: payType, eventId: eventId })
}
export function getModelList() {
  return get(API_BASE + '/ai/getModelList')
}
export function generateImage(data) {
  return post(API_BASE + '/ai/generate', data)
}
export function checkStatus(taskId) {
  return get(API_BASE + '/ai/status', { taskId })
}

// 生成记录（分页，GET，返回数组）
export function getUserCreateLog(page, size) {
  return get(API_BASE + '/user/getUserCreateLog', { page: page, size: size })
}

// 用户基本信息（含余额 balance）
export function getBaseInfo() {
  return get(API_BASE + '/user/getBaseInfo')
}

// ── 上传（走 /upload 代理） ──
export function uploadFile(file) {
  var formData = new FormData()
  formData.append('file', file)
  formData.append('type', '2')
  formData.append('index', '0')
  formData.append('method', 'POST')
  try {
    var userStr = localStorage.getItem('sb-user')
    if (userStr) {
      var uid = JSON.parse(userStr).id
      if (uid) formData.append('userId', uid)
    }
  } catch (e) {}
  return fetch(UPLOAD_BASE + '/user/uploadFile', {
    method: 'POST',
    body: formData
  }).then(function(r) { return r.json() }).then(function(body) {
    var inner = body.data || body
    if (inner && inner.url) inner.thumUrl = inner.thumUrl || inner.url
    return { data: inner, raw: body }
  })
}
